import React, { useState, useEffect } from 'react';
import { agendaService } from '../../../services/agendaService';
import { useAuth } from '../../Admin/context/AuthContext';   // ✅ sesuaikan path
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
    FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaSave,
    FaCheckCircle, FaExclamationTriangle, FaChevronLeft,
    FaChevronRight, FaCalendarAlt, FaMapMarkerAlt, FaStar,
    FaFilter, FaImage, FaFile
} from 'react-icons/fa';

const kategoriOptions = [
    { value: 'umum', label: 'Umum' },
    { value: 'rapat', label: 'Rapat' },
    { value: 'pelatihan', label: 'Pelatihan' },
    { value: 'sosialisasi', label: 'Sosialisasi' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'lainnya', label: 'Lainnya' },
];

const statusKegiatanOptions = [
    { value: 'akan_datang', label: 'Akan Datang' },
    { value: 'berlangsung', label: 'Berlangsung' },
    { value: 'selesai', label: 'Selesai' },
    { value: 'dibatalkan', label: 'Dibatalkan' },
];

const ManajemenAgenda = () => {
    const [dataList, setDataList] = useState([]);
    const [statistics, setStatistics] = useState({});
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterKategori, setFilterKategori] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [gambarFile, setGambarFile] = useState(null);
    const [gambarPreview, setGambarPreview] = useState(null);
    const [lampiranFile, setLampiranFile] = useState(null);

    // ✅ Ambil user dari AuthContext
    const { user } = useAuth();

    // ✅ Helper cek permission
    const hasPermission = (permission) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    };

    const [formData, setFormData] = useState({
        judul: '',
        deskripsi: '',
        konten: '',
        lokasi: '',
        penyelenggara: '',
        tanggal_mulai: '',
        tanggal_selesai: '',
        jam_mulai: '',
        jam_selesai: '',
        kategori: 'umum',
        status_kegiatan: 'akan_datang',
        is_penting: false,
        publish_status: 'publish',
    });

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'blockquote'],
            ['clean']
        ]
    };

    const loadData = async (page = 1) => {
        setLoading(true);
        const params = { page };
        if (searchTerm) params.search = searchTerm;
        if (filterKategori) params.kategori = filterKategori;
        if (filterStatus) params.publish_status = filterStatus;

        const result = await agendaService.getList(params);
        if (result.success) {
            setDataList(result.data.data || []);
            setTotalPages(result.data.last_page || 1);
            setCurrentPage(result.data.current_page || 1);
        }
        setLoading(false);
    };

    const loadStatistics = async () => {
        const result = await agendaService.getStatistics();
        if (result.success) setStatistics(result.data);
    };

    useEffect(() => {
        loadData(1);
        loadStatistics();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            judul: '',
            deskripsi: '',
            konten: '',
            lokasi: '',
            penyelenggara: '',
            tanggal_mulai: new Date().toISOString().split('T')[0],
            tanggal_selesai: '',
            jam_mulai: '',
            jam_selesai: '',
            kategori: 'umum',
            status_kegiatan: 'akan_datang',
            is_penting: false,
            publish_status: 'publish',
        });
        setGambarFile(null);
        setGambarPreview(null);
        setLampiranFile(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            judul: item.judul || '',
            deskripsi: item.deskripsi || '',
            konten: item.konten || '',
            lokasi: item.lokasi || '',
            penyelenggara: item.penyelenggara || '',
            tanggal_mulai: item.tanggal_mulai ? item.tanggal_mulai.split('T')[0] : '',
            tanggal_selesai: item.tanggal_selesai ? item.tanggal_selesai.split('T')[0] : '',
            jam_mulai: item.jam_mulai || '',
            jam_selesai: item.jam_selesai || '',
            kategori: item.kategori || 'umum',
            status_kegiatan: item.status_kegiatan || 'akan_datang',
            is_penting: item.is_penting || false,
            publish_status: item.publish_status || 'publish',
        });
        setGambarFile(null);
        setGambarPreview(item.gambar ? getFileUrl(item.gambar) : null);
        setLampiranFile(null);
        setShowForm(true);
    };

    const getFileUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${path}`;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleGambarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setGambarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setGambarPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const submitData = new FormData();
        Object.keys(formData).forEach(key => {
            if (key === 'is_penting') {
                submitData.append(key, formData[key] ? 1 : 0);
            } else {
                submitData.append(key, formData[key] || '');
            }
        });

        if (gambarFile) submitData.append('gambar', gambarFile);
        if (lampiranFile) submitData.append('file_lampiran', lampiranFile);

        let result;
        if (editing) {
            result = await agendaService.update(editing.id, submitData);
        } else {
            result = await agendaService.create(submitData);
        }

        if (result.success) {
            showNotif(result.message || 'Agenda berhasil disimpan');
            setShowForm(false);
            loadData(currentPage);
            loadStatistics();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await agendaService.delete(deleteConfirm.id);
        if (result.success) {
            showNotif('Agenda berhasil dihapus');
            loadData(currentPage);
            loadStatistics();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Agenda</h2>
                    <p className="text-gray-600">Kelola jadwal kegiatan BPKAD</p>
                </div>
                {/* ✅ Tombol Tambah — cek permission */}
                {hasPermission('agenda.create') && (
                    <button onClick={handleCreate} className="btn-primary flex items-center justify-center">
                        <FaPlus className="mr-2" /> Tambah Agenda
                    </button>
                )}
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                {[
                    { label: 'Total', value: statistics.total, color: 'text-[#0F4C3A]' },
                    { label: 'Publish', value: statistics.publish, color: 'text-green-600' },
                    { label: 'Akan Datang', value: statistics.akan_datang, color: 'text-blue-600' },
                    { label: 'Berlangsung', value: statistics.berlangsung, color: 'text-purple-600' },
                    { label: 'Selesai', value: statistics.selesai, color: 'text-gray-500' },
                    { label: 'Penting', value: statistics.is_penting, color: 'text-red-500' },
                ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 shadow-sm text-center">
                        <p className={`text-2xl font-bold ${stat.color}`}>{stat.value || 0}</p>
                        <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                ))}
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success' ? <FaCheckCircle className="mr-2" /> : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari agenda..."
                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    />
                </div>
                <select value={filterKategori} onChange={(e) => setFilterKategori(e.target.value)}
                    className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]">
                    <option value="">Semua Kategori</option>
                    {kategoriOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]">
                    <option value="">Semua Status</option>
                    <option value="publish">Publish</option>
                    <option value="draft">Draft</option>
                    <option value="arsip">Arsip</option>
                </select>
                <button onClick={() => loadData(1)} className="btn-primary">
                    <FaFilter className="inline mr-1" /> Filter
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4">No</th>
                                <th className="p-4">Agenda</th>
                                <th className="p-4">Tanggal & Waktu</th>
                                <th className="p-4">Lokasi</th>
                                <th className="p-4">Kategori</th>
                                <th className="p-4">Status</th>
                                <th className="p-4 text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} className="p-8 text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                                </td></tr>
                            ) : dataList.length === 0 ? (
                                <tr><td colSpan={7} className="p-8 text-center text-gray-500">Belum ada agenda</td></tr>
                            ) : (
                                dataList.map((item, index) => (
                                    <tr key={item.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4 text-gray-500">
                                            {(currentPage - 1) * 15 + index + 1}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-start gap-2">
                                                {item.is_penting && (
                                                    <FaStar className="text-yellow-500 mt-1 flex-shrink-0" title="Penting" />
                                                )}
                                                <div>
                                                    <p className="font-semibold text-[#0F4C3A] line-clamp-2">{item.judul}</p>
                                                    <p className="text-xs text-gray-500 capitalize">{item.kategori}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">
                                            <div className="flex items-center gap-1">
                                                <FaCalendarAlt className="text-[#2E7D32] text-xs" />
                                                {formatDate(item.tanggal_mulai)}
                                            </div>
                                            {item.jam_mulai && (
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {item.jam_mulai} WIB
                                                </p>
                                            )}
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">
                                            {item.lokasi && (
                                                <div className="flex items-start gap-1">
                                                    <FaMapMarkerAlt className="text-[#2E7D32] text-xs mt-1 flex-shrink-0" />
                                                    <span className="line-clamp-2">{item.lokasi}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className="bg-blue-50 text-blue-600 text-xs px-2 py-1 rounded-full capitalize">
                                                {item.kategori}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <span className={`text-xs px-2 py-1 rounded-full ${item.publish_status === 'publish' ? 'bg-green-100 text-green-700' :
                                                item.publish_status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-gray-100 text-gray-500'
                                                }`}>
                                                {item.publish_status}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center gap-2">
                                                {/* ✅ Tombol Edit — cek permission */}
                                                {hasPermission('agenda.update') && (
                                                    <button onClick={() => handleEdit(item)}
                                                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg" title="Edit">
                                                        <FaEdit />
                                                    </button>
                                                )}

                                                {/* ✅ Tombol Hapus — cek permission */}
                                                {hasPermission('agenda.delete') && (
                                                    <button onClick={() => setDeleteConfirm(item)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Hapus">
                                                        <FaTrash />
                                                    </button>
                                                )}

                                                {/* Fallback */}
                                                {!hasPermission('agenda.update') && !hasPermission('agenda.delete') && (
                                                    <span className="text-xs text-gray-400 italic">-</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <p className="text-sm text-gray-500">Halaman {currentPage} dari {totalPages}</p>
                        <div className="flex gap-2">
                            <button onClick={() => loadData(currentPage - 1)} disabled={currentPage === 1}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                                <FaChevronLeft />
                            </button>
                            <button onClick={() => loadData(currentPage + 1)} disabled={currentPage === totalPages}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-4xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-xl font-bold text-[#0F4C3A]">
                                {editing ? 'Edit Agenda' : 'Tambah Agenda'}
                            </h3>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Judul *</label>
                                    <input type="text" name="judul" value={formData.judul}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        required />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Kategori *</label>
                                        <select name="kategori" value={formData.kategori} onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]">
                                            {kategoriOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status Kegiatan *</label>
                                        <select name="status_kegiatan" value={formData.status_kegiatan} onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]">
                                            {statusKegiatanOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Mulai *</label>
                                        <input type="date" name="tanggal_mulai" value={formData.tanggal_mulai}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            required />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Selesai</label>
                                        <input type="date" name="tanggal_selesai" value={formData.tanggal_selesai}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Jam Mulai</label>
                                        <input type="time" name="jam_mulai" value={formData.jam_mulai}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Jam Selesai</label>
                                        <input type="time" name="jam_selesai" value={formData.jam_selesai}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lokasi</label>
                                        <input type="text" name="lokasi" value={formData.lokasi}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: Aula BPKAD" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Penyelenggara</label>
                                        <input type="text" name="penyelenggara" value={formData.penyelenggara}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: Bidang Aset" />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi Singkat</label>
                                    <textarea name="deskripsi" value={formData.deskripsi} onChange={handleInputChange}
                                        rows="2" className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Ringkasan singkat agenda..." />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Konten Detail</label>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.konten}
                                        onChange={(val) => setFormData(prev => ({ ...prev, konten: val }))}
                                        modules={modules}
                                        placeholder="Tulis detail agenda di sini..."
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FaImage className="inline mr-1" /> Gambar (Max 5MB)
                                        </label>
                                        <input type="file" accept="image/*" onChange={handleGambarChange}
                                            className="w-full px-4 py-2.5 border rounded-lg" />
                                        {gambarPreview && (
                                            <img src={gambarPreview} alt="Preview" className="mt-2 max-h-32 rounded border" />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FaFile className="inline mr-1" /> File Lampiran (Max 10MB)
                                        </label>
                                        <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                                            onChange={(e) => setLampiranFile(e.target.files[0])}
                                            className="w-full px-4 py-2.5 border rounded-lg" />
                                        {lampiranFile && (
                                            <p className="mt-1 text-sm text-green-600">✓ {lampiranFile.name}</p>
                                        )}
                                        {editing?.file_name && !lampiranFile && (
                                            <p className="mt-1 text-sm text-gray-500">File: {editing.file_name}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer">
                                        <input type="checkbox" name="is_penting" checked={formData.is_penting}
                                            onChange={handleInputChange} className="mr-2" />
                                        <FaStar className="text-yellow-500 mr-2" />
                                        <span className="text-sm font-medium">Agenda Penting</span>
                                    </label>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Publish Status</label>
                                        <select name="publish_status" value={formData.publish_status} onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]">
                                            <option value="publish">Publish (Tampil)</option>
                                            <option value="draft">Draft</option>
                                            <option value="arsip">Arsip</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white">
                                    <button type="button" onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                                        Batal
                                    </button>
                                    <button type="submit" disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50">
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Menyimpan...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editing ? 'Update' : 'Simpan'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Agenda?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus <strong>{deleteConfirm.judul}</strong>
                        </p>
                        <div className="flex justify-center gap-3">
                            <button onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50">
                                Batal
                            </button>
                            <button onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600">
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenAgenda;